import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { HttpDataService, IHttpDataCache } from '../http/http-data.service';
import { LOV_INVOICE_CURRENCY, LOV_YES_NO, LovService } from '../services/lov.service';
import { IBasicContact, IBPProfileBasic, ICodeValue, ICompany, ICompanyGroup, IContact, IDepartment, INamedObject, IObject } from '@type_definitions/datatypes';
import { IReportMetadata } from './report-data.service';
import { IStoredReport } from './stored-report-data.service';
import { urlUtil } from '../util';
import { IRoleContact } from '@app/main/webshop/info/webshop-info-data.service';

export interface IDashboardMetadataReports {
  [key:string]:IReportMetadata;
}

export interface IDashboardStoredReports {
  [key:string]:IStoredReport;
}

export interface ICustomerDashboardInfo {
  logo:string;
  webshopId:number;
  roleContacts:IRoleContact[];
  focusContactType:ICodeValue;
}

const MAX_REPORT_REQUESTS:number = 20;
@Injectable()
export class DashboardDataService {
  private cache: IHttpDataCache<any>;

  constructor(
    private httpDataService: HttpDataService,
    private lovService: LovService
  ) {
    this.cache = httpDataService.createCache();
  }

  public companyGroups():Promise<ICompanyGroup[]> {
    return this.cache.doGET('rest/compman/customercompanygroups', undefined, undefined, 'Error loading Customer company groups');
  }

  public currencies():Promise<any[]> {
    return this.lovService.list(LOV_INVOICE_CURRENCY);
  }

  public yesNo():Promise<any[]> {
    return this.lovService.list(LOV_YES_NO);
  }

  public BPs():Promise<IBPProfileBasic[]> {
    return this.cache.doGET('rest/bpprofiles/profiles/', undefined, undefined, 'Error loading BPs');
  }

  public LAMs():Promise<INamedObject[]> {
    return this.cache.doGET('rest/compman/activelams', undefined, undefined, 'Error loading LAMs');
  }

  public LAMCompanies():Promise<ICompany[]> {
    return this.cache.doGET('rest/compman/activelamcompanies', undefined, undefined, 'Error loading LAM Companies');
  }

  public LAMContacts():Promise<ICompany[]> {
    return this.cache.doGET('rest/compman/activelamcontacts', undefined, undefined, 'Error loading LAM Contacts');
  }

  public gbdms():Promise<IBasicContact[]> {
    return this.cache.doGET('rest/acslinfo/activeacslusers', undefined, undefined, 'Error loading GBDMS');
  }

  public ODs():Promise<IContact[]> {
    return this.cache.doGET('rest/acslinfo/ods', undefined, undefined, 'Error loading PSS');
  }

  public departments(companyId:number):Promise<IDepartment[]> {
    return this.cache.doGET(`rest/compman/company/${companyId}/departments`, undefined, undefined, 'Error loading Departments');
  }

  public gbdmDesks():Promise<IContact[]> {
    return this.cache.doGET('rest/acslinfo/gbdmdesks', undefined, undefined, 'Error loading desks');
  }

  public odTeams():Promise<IDepartment[]> {
    return this.cache.doGET(`rest/acslinfo/odteams`, undefined, undefined, 'Error loading BP Desk');
  }

  public customerDashboardInfo(webshopId:number, companyId:number):Promise<ICustomerDashboardInfo> {
    return this.cache.doGET('rest/reports/dashboard/customerdashboardinfo', { webshopId, companyId }, undefined, 'Error loading customer info');
  }

  public loadReportsMetadata(reportIds:number[]):Promise<IDashboardMetadataReports> {
    return this.doGET<IDashboardMetadataReports>('rest/reports/dashboard/metadata', { reportId: reportIds }, 'Dashboard metadata failed to load');
  }

  public loadStoredReports(storedReportCodes:string[]):Promise<IDashboardStoredReports> {
    return this.doGET<IDashboardStoredReports>('rest/reports/dashboard/stored', { storedReportCode: storedReportCodes }, 'Stored reports failed to load');
  }

  // the API supports max 20 query parameters in one go, therefore this method will perform additional REST calls when the query params limit has been reached, merge and return the results in a single object
  private doGET<T>(url:string, queryParams:IObject, errorMessage:string, maxParams:number = MAX_REPORT_REQUESTS):Promise<T> {
    const queryParamKey:string = Object.keys(queryParams)[0];
    const queryParamValues:number[] = Object.values(queryParams)[0];
    const numRequests:number = Math.ceil(queryParamValues.length / maxParams);
    const promises:Promise<void|string[]>[] = [];

    const remainingQueryParams:number[] = _.clone(queryParamValues);
    for (let i:number = 0; i < numRequests; i++) {
      const reqReportIds:number[] = remainingQueryParams.splice(0, maxParams);
      promises.push(
        this.httpDataService.doGET(
          urlUtil.addQueryParams(url, { [queryParamKey]: reqReportIds }),
          undefined,
          undefined,
          errorMessage
        )
      );
    }

    const allResults:T = <any>{};
    return Promise.all(promises)
      .then((promisesResults:any) => {
        _.chain(promisesResults)
          .each((promiseResults:IReportMetadata[]) => {
            _.each(promiseResults, (promiseResult:IReportMetadata) => allResults[promiseResult.id] = promiseResult);
          });

        return allResults;
      });
  }

  public clear() {
    this.cache.clear();
  }
}